/**
 * Main Orbit Blocks React App
 */

import React, { useState, useEffect } from 'react';
import OrbitAPI from '../utils/api';
import FilterBar from './FilterBar';
import BlocksGrid from './BlocksGrid';
import Notifications from './Notifications';

function App({ mode = 'admin' }) {
	// Mode can be 'admin' or 'public'
	// Public mode hides install/sync features
	const isPublic = mode === 'public';
	const config = window.orbitConfig || {};

	const [blocks, setBlocks] = useState([]);
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [filter, setFilter] = useState('all');
	const [search, setSearch] = useState('');
	const [installing, setInstalling] = useState(new Set());
	const [notifications, setNotifications] = useState([]);
	const [updates, setUpdates] = useState([]);
	const [bulkActionModal, setBulkActionModal] = useState(null);
	const [bulkProgress, setBulkProgress] = useState(null);
	const [visibleCount, setVisibleCount] = useState(30); // Lazy loading - show 30 initially

	const api = new OrbitAPI();

	// Fetch initial data
	useEffect(() => {
		loadData();
	}, []);

	const loadData = async () => {
		setLoading(true);
		setError(null);

		try {
			// API Calls
			const manifestData = await api.getManifest();

			setBlocks(manifestData || []);
			setLoading(false); // Hide loading state early

			// In public mode, extract categories from blocks
			if (isPublic && manifestData) {
				const uniqueCategories = new Set();
				manifestData.forEach(block => {
					if (block.categories && Array.isArray(block.categories)) {
						block.categories.forEach(cat => {
							const categoryValue = typeof cat === 'string' ? cat : (cat.slug || cat.name);
							if (categoryValue) uniqueCategories.add(categoryValue);
						});
					}
				});
				setCategories(Array.from(uniqueCategories).sort());
			} else {
				// Admin mode: Load categories and updates in background
				Promise.all([
					api.getCategories().catch(err => {
						console.warn('Categories failed:', err);
						return [];
					}),
					api.checkUpdates().catch(err => {
						console.warn('Updates failed:', err);
						return { updates: [] };
					})
				]).then(([categoriesData, updatesData]) => {
					setCategories(categoriesData || []);
					setUpdates(updatesData.updates || []);
				});
			}

		} catch (err) {
			setError(err.message || 'Connection failed. Please try again.');
			console.error('Failed to load data:', err);
		} finally {
			setLoading(false);
		}
	};
	
	// Merge updates with blocks
	const blocksWithUpdates = blocks.map(block => {
		const hasUpdate = updates.some(update => update.slug === block.slug);
		const updateInfo = updates.find(update => update.slug === block.slug);
		
		return {
			...block,
			has_update: hasUpdate,
			update_info: updateInfo
		};
	});

	// Filter blocks based on category and search
	const filteredBlocks = blocks
	.filter(block => {
		// Category filter
		if (filter !== 'all' && !block.categories?.includes(filter)) {
			return false;
		}

		// Enhanced search - searches across multiple fields
		if (search) {
			const searchLower = search.toLowerCase();

			// Search in block name
			if (block.name?.toLowerCase().includes(searchLower)) {
				return true;
			}

			// Search in block slug
			if (block.slug?.toLowerCase().includes(searchLower)) {
				return true;
			}

			// Search in categories
			if (block.categories && Array.isArray(block.categories)) {
				const categoryMatch = block.categories.some(cat => {
					const catValue = typeof cat === 'string' ? cat : (cat.name || cat.slug || '');
					return catValue.toLowerCase().includes(searchLower);
				});
				if (categoryMatch) return true;
			}

			// Search in description if available
			if (block.description?.toLowerCase().includes(searchLower)) {
				return true;
			}

			// Search in type if available
			if (block.type?.toLowerCase().includes(searchLower)) {
				return true;
			}

			// Search in keywords/tags if available
			if (block.keywords && Array.isArray(block.keywords)) {
				const keywordMatch = block.keywords.some(keyword =>
					keyword.toLowerCase().includes(searchLower)
				);
				if (keywordMatch) return true;
			}

			// No match found
			return false;
		}

		return true;
	})
	.map(block => {
		// Add update information to each block
		const hasUpdate = updates.some(update => update.slug === block.slug);
		const updateInfo = updates.find(update => update.slug === block.slug);
		
		return {
			...block,
			has_update: hasUpdate,
			update_info: updateInfo
		};
	})
	.sort((a, b) => {
		// Your existing sort logic
		if (filter === 'all') {
			if (a.installed && !b.installed) return -1;
			if (!a.installed && b.installed) return 1;
		}
		return 0;
	});

	// Reset visible count when search or filter changes
	useEffect(() => {
		setVisibleCount(30);
	}, [search, filter]);

	// Slice blocks for lazy loading
	const visibleBlocks = filteredBlocks.slice(0, visibleCount);
	const hasMore = filteredBlocks.length > visibleCount;

	const loadMore = () => {
		setVisibleCount(prev => prev + 30);
	};

	// Handle block installation
	const handleInstall = async (slug) => {
		if (installing.has(slug)) return;
		
		setInstalling(prev => new Set(prev).add(slug));
		
		try {
			await api.installBlock(slug);
			
			// Update block as installed
			setBlocks(prev => prev.map(block => 
				block.slug === slug 
					? { ...block, installed: true }
					: block
			));
			
			addNotification(`Block "${slug}" installed successfully!`, 'success');
		} catch (err) {
			addNotification(`Failed to install block: ${err.message}`, 'error');
		} finally {
			setInstalling(prev => {
				const next = new Set(prev);
				next.delete(slug);
				return next;
			});
		}
	};

	// Handle block removal
	const handleRemove = async (slug) => {
		if (installing.has(slug)) return;
		
		setInstalling(prev => new Set(prev).add(slug));
		
		try {
			await api.removeBlock(slug);
			
			// Update block as not installed
			setBlocks(prev => prev.map(block => 
				block.slug === slug 
					? { ...block, installed: false }
					: block
			));
			
			addNotification(`Block "${slug}" removed successfully!`, 'success');
		} catch (err) {
			addNotification(`Failed to remove block: ${err.message}`, 'error');
		} finally {
			setInstalling(prev => {
				const next = new Set(prev);
				next.delete(slug);
				return next;
			});
		}
	};
	
	// Update handler
	const handleUpdate = async (slug, overwrite = false) => {
		if (installing.has(slug)) return;
		
		setInstalling(prev => new Set(prev).add(slug));
		
		try {
			await api.updateBlock(slug, overwrite);
			
			// Reload data to get fresh status
			const manifestData = await api.getManifest();
			setBlocks(manifestData || []);
			
			const updatesData = await api.checkUpdates();
			setUpdates(updatesData.updates || []);
			
			addNotification(`Block "${slug}" updated successfully!`, 'success');
		} catch (err) {
			addNotification(err.message || 'Update failed. Please try again.', 'error');
		} finally {
			setInstalling(prev => {
				const next = new Set(prev);
				next.delete(slug);
				return next;
			});
		}
	};

	// Bulk actions
	const handleInstallAll = () => {
		const count = blocks.filter(b => !b.installed).length;
		setBulkActionModal({
			type: 'install',
			title: 'Install All Blocks',
			message: `Install all ${count} available blocks?`,
			warning: 'This may take a few moments.',
			action: async () => {
				setBulkActionModal(null);
				const blocksToInstall = blocks.filter(b => !b.installed);
				await executeBulkAction('install', blocksToInstall, handleInstall);
			}
		});
	};

	const handleRemoveAll = () => {
		const count = blocks.filter(b => b.installed).length;
		setBulkActionModal({
			type: 'remove',
			title: 'Remove All Blocks',
			message: `Remove all ${count} installed blocks?`,
			warning: 'This will permanently delete all blocks from your Elementor library. This cannot be undone.',
			action: async () => {
				setBulkActionModal(null);
				const blocksToRemove = blocks.filter(b => b.installed);
				await executeBulkAction('remove', blocksToRemove, handleRemove);
			}
		});
	};

	const handleUpdateAll = () => {
		const count = blocksWithUpdates.filter(b => b.has_update).length;
		setBulkActionModal({
			type: 'update',
			title: 'Update All Blocks',
			message: `Update all ${count} blocks that have updates available?`,
			warning: 'Existing customizations may be overwritten.',
			action: async () => {
				setBulkActionModal(null);
				const blocksToUpdate = blocksWithUpdates.filter(b => b.has_update);
				await executeBulkAction('update', blocksToUpdate, (block) => handleUpdate(block.slug, false));
			}
		});
	};

	// Execute bulk action - SIMPLE sequential processing
	const executeBulkAction = async (type, blocksList, actionHandler) => {
		let cancelled = false;
		let completed = 0;
		let succeeded = 0;
		const abortController = new AbortController();

		setBulkProgress({
			type,
			total: blocksList.length,
			current: 0,
			currentBlock: '',
			onCancel: () => {
				cancelled = true;
				abortController.abort(); // Abort any in-flight request immediately
			}
		});

		// Process ONE block at a time
		for (let i = 0; i < blocksList.length; i++) {
			if (cancelled) {
				setBulkProgress(null);
				addNotification(`${type.charAt(0).toUpperCase() + type.slice(1)} cancelled after ${completed} of ${blocksList.length} blocks`, 'warning');

				// Reload manifest to show accurate state
				try {
					const manifestData = await api.getManifest(true);
					setBlocks(manifestData || []);
				} catch (err) {
					console.error('Failed to reload manifest:', err);
				}
				return;
			}

			const block = blocksList[i];

			// Update progress
			setBulkProgress(prev => ({
				...prev,
				current: completed,
				currentBlock: block.slug
			}));

			try {
				// Call individual API
				if (type === 'install') {
					await api.installBlock(block.slug);
				} else if (type === 'remove') {
					await api.removeBlock(block.slug);
				} else if (type === 'update') {
					await api.updateBlock(block.slug, true);
				}

				succeeded++;

				// Update local state immediately so stats update
				setBlocks(prev => prev.map(b => {
					if (b.slug === block.slug) {
						if (type === 'install') {
							return { ...b, installed: true };
						} else if (type === 'remove') {
							return { ...b, installed: false };
						}
					}
					return b;
				}));
			} catch (err) {
				console.error(`Failed to ${type} ${block.slug}:`, err);
			}

			completed++;

			// Update progress count
			setBulkProgress(prev => prev ? ({
				...prev,
				current: completed
			}) : null);
		}

		setBulkProgress(null);

		// Reload manifest at the end
		try {
			const manifestData = await api.getManifest(true);
			setBlocks(manifestData || []);

			// If we just updated blocks, refresh the updates list
			if (type === 'update') {
				const updatesData = await api.checkUpdates();
				setUpdates(updatesData.updates || []);
			}
		} catch (err) {
			console.error('Failed to reload manifest:', err);
		}

		if (!cancelled) {
			const actionText = type === 'install' ? 'installed' : type === 'remove' ? 'removed' : 'updated';
			addNotification(`Successfully ${actionText} ${succeeded} of ${blocksList.length} blocks!`, 'success');
		}
	};

	// Helper to batch update block statuses
	const updateBlocksStatus = (results, type) => {
		const successfulSlugs = results.filter(r => r.success).map(r => r.slug);

		setBlocks(prev => prev.map(block => {
			if (successfulSlugs.includes(block.slug)) {
				if (type === 'install') {
					return { ...block, installed: true };
				} else if (type === 'remove') {
					return { ...block, installed: false };
				}
			}
			return block;
		}));
	};

	// Add notification
	const addNotification = (message, type) => {
		const id = Date.now();
		setNotifications(prev => [...prev, { id, message, type }]);
		
		// Auto remove after 4 seconds
		setTimeout(() => {
			removeNotification(id);
		}, 4000);
	};

	// Remove notification
	const removeNotification = (id) => {
		setNotifications(prev => prev.filter(n => n.id !== id));
	};

	// Get statistics
	const stats = {
		total: blocks.length,
		installed: blocks.filter(b => b.installed).length,
		available: blocks.filter(b => !b.installed).length,
		filtered: filteredBlocks.length
	};

	// Loading state
	if (loading) {
		return (
			<div className="or-text-center or-py-12">
				<div className="or-inline-flex or-items-center or-justify-center or-w-20 or-h-20 or-bg-gradient-to-r or-from-primary-500 or-to-primary-600 or-rounded-full or-mb-4 or-shadow-lg">
					<div className="or-animate-spin or-rounded-full or-h-16 or-w-16 or-border-4 or-border-white or-border-t-transparent"></div>
				</div>
				<p className="or-text-gray-600 or-font-medium or-text-lg">Loading blocks from the Orbit library...</p>
			</div>
		);
	}

	// Error state
	if (error) {
		const isLicenseError = error.includes('license') || error.includes('License');
		
		return (
			<div className="or-rounded-lg or-border or-p-6 or-bg-red-50 or-border-red-200 or-text-red-800">
				<h3 className="or-font-semibold or-mb-2 or-text-lg">
					{isLicenseError ? 'License Configuration Required' : 'Connection Error'}
				</h3>
				
				<p className="or-mb-4">{error}</p>
				
				<div className="or-flex or-flex-wrap or-gap-3">
					<button className="button button-primary" onClick={loadData}>
						Try Again
					</button>
					
					{isLicenseError && (
						<a className="button button-primary" href="/wp-admin/admin.php?page=orbit-settings">
							Configure License
						</a>
					)}
					
					{!isLicenseError && (
						<button className="button" onClick={() => setError(null)}>
							Dismiss
						</button>
					)}
				</div>
			</div>
		);
	}

	// Main app render
	return (
		<div className="orbit-blocks-app orbit-page">
			<Notifications
				notifications={notifications}
				onRemove={removeNotification}
			/>
			
				<FilterBar
				categories={categories}
				filter={filter}
				onFilterChange={setFilter}
				search={search}
				onSearchChange={setSearch}
				stats={stats}
				mode={mode}
				onInstallAll={!isPublic ? handleInstallAll : undefined}
				onRemoveAll={!isPublic ? handleRemoveAll : undefined}
				onUpdateAll={!isPublic ? handleUpdateAll : undefined}
				hasUpdates={!isPublic && blocksWithUpdates.some(b => b.has_update)}
			/>
			
			<BlocksGrid
				blocks={visibleBlocks}
				installing={installing}
				mode={mode}
				onInstall={!isPublic ? handleInstall : undefined}
				onRemove={!isPublic ? handleRemove : undefined}
				onUpdate={!isPublic ? handleUpdate : undefined}
				stats={stats}
			/>

			{/* Load More Button */}
			{hasMore && (
				<div className="or-text-center or-py-8">
					<button
						onClick={loadMore}
						className="button button-primary button-large or-px-8"
					>
						Load More Blocks ({filteredBlocks.length - visibleCount} remaining)
					</button>
				</div>
			)}

			{/* Bulk Action Modal */}
			{bulkActionModal && (
				<div className="or-fixed or-inset-0 or-bg-black or-bg-opacity-50 or-flex or-items-center or-justify-center or-z-50">
					<div className="or-bg-white or-rounded-lg or-p-6 or-max-w-md or-mx-4">
						<h3 className="or-text-lg or-font-semibold or-text-gray-900 or-mb-3">
							{bulkActionModal.title}
						</h3>

						<p className="or-text-gray-600 or-mb-2">
							{bulkActionModal.message}
						</p>

						<div className={`or-rounded or-p-3 or-mb-4 ${
							bulkActionModal.type === 'remove'
								? 'or-bg-yellow-50 or-border or-border-yellow-200'
								: 'or-bg-blue-50 or-border or-border-blue-200'
						}`}>
							<p className={`or-text-sm ${
								bulkActionModal.type === 'remove' ? 'or-text-yellow-800' : 'or-text-blue-800'
							}`}>
								{bulkActionModal.type === 'remove' ? '⚠️' : 'ℹ️'} <strong>
									{bulkActionModal.type === 'remove' ? 'Warning:' : 'Note:'}
								</strong> {bulkActionModal.warning}
							</p>
						</div>

						<div className="or-flex or-justify-end or-gap-3">
							<button
								onClick={() => setBulkActionModal(null)}
								className="or-px-4 or-py-2 or-bg-gray-100 or-text-gray-700 or-rounded hover:or-bg-gray-200"
							>
								Cancel
							</button>
							<button
								onClick={bulkActionModal.action}
								className={`or-px-4 or-py-2 or-text-white or-rounded ${
									bulkActionModal.type === 'remove'
										? 'or-bg-red-600 hover:or-bg-red-700'
										: 'or-bg-primary-600 hover:or-bg-primary-700'
								}`}
							>
								{bulkActionModal.type === 'remove' ? 'Remove All' :
								 bulkActionModal.type === 'update' ? 'Update All' : 'Install All'}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Bulk Progress Modal */}
			{bulkProgress && (
				<div className="or-fixed or-inset-0 or-bg-black or-bg-opacity-50 or-flex or-items-center or-justify-center or-z-50">
					<div className="or-bg-white or-rounded-lg or-p-6 or-max-w-md or-mx-4">
						<h3 className="or-text-lg or-font-semibold or-text-gray-900 or-mb-3">
							{bulkProgress.type === 'install' ? 'Installing Blocks' :
							 bulkProgress.type === 'remove' ? 'Removing Blocks' : 'Updating Blocks'}
						</h3>

						<div className="or-mb-4">
							<div className="or-flex or-justify-between or-text-sm or-text-gray-600 or-mb-2">
								<span>Progress: {bulkProgress.current} of {bulkProgress.total}</span>
								<span>{Math.round((bulkProgress.current / bulkProgress.total) * 100)}%</span>
							</div>

							<div className="or-w-full or-bg-gray-200 or-rounded-full or-h-3 or-overflow-hidden">
								<div
									className="or-bg-primary-600 or-h-3 or-rounded-full or-transition-all or-duration-300"
									style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
								></div>
							</div>
						</div>

						{bulkProgress.currentBlock && (
							<div className="or-bg-gray-50 or-rounded or-p-3 or-mb-4">
								<p className="or-text-sm or-text-gray-600 or-mb-1">
									Currently processing:
								</p>
								<p className="or-font-medium or-text-gray-900 or-truncate" title={bulkProgress.currentBlock}>
									{bulkProgress.currentBlock}
								</p>
							</div>
						)}

						<button
							onClick={bulkProgress.onCancel}
							className="or-w-full or-px-4 or-py-2 or-bg-red-600 or-text-white or-rounded hover:or-bg-red-700"
						>
							Cancel Operation
						</button>
					</div>
				</div>
			)}
		</div>
	);
}

export default App;